'use client'

import { useState, useMemo } from 'react'
import clsx from 'clsx'
import { ChevronDown } from 'lucide-react'
import {
  HsTaxListResponse,
  HsTaxCommonResponse,
} from '@/modules/hs/types/hs-tax.response'
import { HsTaxHangNgach } from '@/modules/hs/types/hs-response'
import { TaxTable, HsTaxCountry, TaxNote } from '@/modules/hs/types/tax.types'
import NKUDDB_FORM from '@/modules/hs/constants/nkuddb-form.json'
import { countries } from '@/modules/hs/constants/country.constant'
import TaxRateChartv2 from './component/HsTaxChartv2'
import HsTaxModalHandler from './component/HsTaxModalHandler'
import { lookupTaxNote } from '@/modules/hs/utils/hstax-note.util'

// Helper to get country name by ID
const getCountryNameById = (countryId: number | string): string => {
  const id = typeof countryId === 'string' ? parseInt(countryId, 10) : countryId
  const country = countries.find(c => c.id === id)
  return country?.vietnameseName || country?.value || `ID: ${countryId}`
}

interface HsTaxFTAProps {
  taxList: HsTaxListResponse
  tables: TaxTable[]
  taxByCountries: Record<number, HsTaxCountry[]>
  notes: TaxNote[]
  hangngach?: HsTaxHangNgach[]
}

const currentDate = new Date()

// Helper function to check if tax is effective now
const isEffectiveNow = (
  effectiveFrom: string,
  effectiveTo: string,
  currentDate: Date
) => {
  const fromDate = new Date(effectiveFrom)
  const toDate = new Date(effectiveTo)
  return currentDate >= fromDate && currentDate <= toDate
}

// Helper to get current effective tax
const getCurrentTax = (taxes: HsTaxCommonResponse[]) => {
  return taxes.find((tax) =>
    isEffectiveNow(tax.effectiveFrom, tax.effectiveTo, currentDate)
  )
}

export default function HsTaxFTA({
  taxList,
  tables,
  taxByCountries,
  notes,
  hangngach,
}: HsTaxFTAProps) {
  const [expandedKey, setExpandedKey] = useState<string | null>(null)

  // Transform NKUDDB data into displayable format
  const ftaData = useMemo(() => {
    if (!taxList.nkuddb) return []

    const data: Array<{
      key: string
      form: string
      agreement: string
      rate: number | null
      effectiveFrom: string | null
      effectiveTo: string | null
      countries: HsTaxCountry[]
      excludedCountryNames: string[]
      excludedCountryIds: Set<number>
      table?: TaxTable
      taxData: HsTaxCommonResponse[]
      isNotPreferential: boolean
      conditions: string[]
      noteText: string | null
    }> = []

    Object.entries(taxList.nkuddb).forEach(([key, taxes]) => {
      if (!taxes || taxes.length === 0) return

      const formInfo = NKUDDB_FORM.find((f) => f.key === key)
      if (!formInfo) return

      const currentTax = getCurrentTax(taxes)
      if (!currentTax) return

      const isNotPreferential = currentTax.taxRate < 0
      const table = tables.find((t) => t.abbreviation === key)
      const allCountries = currentTax?.taxId
        ? taxByCountries[Number(currentTax.taxId)] || []
        : []

      // Get excluded countries for this tax
      const taxIdKey = String(currentTax.taxId)
      const excludedCountryIdsRaw = currentTax?.taxId
        ? (taxList.excludeCountries?.[taxIdKey] || taxList.excludeCountries?.[Number(currentTax.taxId)] || [])
        : []
      const excludedCountryIds = new Set<number>(
        excludedCountryIdsRaw.map((id: number | string) => typeof id === 'string' ? parseInt(id, 10) : id)
      )
      const excludedCountryNames = Array.from(excludedCountryIds).map(id => getCountryNameById(id))

      // Filter out excluded countries from the applied countries list
      const countries = allCountries.filter(c => !excludedCountryIds.has(c.countryId))

      // Get note text if tax rate is negative
      const noteText = isNotPreferential
        ? lookupTaxNote(currentTax.taxRate, notes) ?? null
        : null

      data.push({
        key,
        form: formInfo.form,
        agreement: formInfo.name,
        rate: currentTax.taxRate,
        effectiveFrom: currentTax.effectiveFrom,
        effectiveTo: currentTax.effectiveTo,
        countries,
        excludedCountryNames,
        excludedCountryIds,
        table,
        taxData: taxes,
        isNotPreferential,
        conditions: formInfo.conditions || [],
        noteText,
      })
    })

    // Sort by rate ascending
    return data.sort((a, b) => (a.rate ?? 999) - (b.rate ?? 999))
  }, [taxList.nkuddb, tables, taxByCountries, taxList.excludeCountries])

  if (ftaData.length === 0) {
    return null
  }

  return (
    <section id="fta-section" className="mb-6">
      <div className="cl-card-border">
        {/* Section Header */}
        <div className="mb-6 border-b-2 border-[#f5f5f5] pb-3">
          <h2 className="mb-1 flex items-center gap-2.5 text-lg font-bold">
            <span className="text-[#1E40AF]">
              <svg
                className="size-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </span>
            Thuế FTA và Tính thuế
          </h2>
          <p className="text-sm text-[#525252]">
            Chi tiết thuế nhập khẩu ưu đãi đặc biệt theo từng hiệp định FTA mới nhất và Công cụ tính thuế
          </p>
        </div>

        {/* FTA List */}
        <div className="space-y-4">
          {ftaData.map((item) => (
            <FTACard
              key={item.key}
              item={item}
              isExpanded={expandedKey === item.key}
              onToggle={() =>
                setExpandedKey(expandedKey === item.key ? null : item.key)
              }
            />
          ))}
        </div>
      </div>

      {/* Tax Modal Handler */}
      <HsTaxModalHandler
        taxList={taxList}
        notes={notes}
        hangngach={hangngach}
      />
    </section>
  )
}

// FTA Card Component
function FTACard({
  item,
  isExpanded,
  onToggle,
}: {
  item: {
    key: string
    form: string
    agreement: string
    rate: number | null
    effectiveFrom: string | null
    effectiveTo: string | null
    countries: HsTaxCountry[]
    excludedCountryNames: string[]
    excludedCountryIds: Set<number>
    table?: TaxTable
    taxData: HsTaxCommonResponse[]
    isNotPreferential: boolean
    conditions: string[]
    noteText: string | null
  }
  isExpanded: boolean
  onToggle: () => void
}) {
  // Get tax history for chart
  const taxHistory = useMemo(() => {
    return item.taxData
      .map((tax) => ({
        year: new Date(tax.effectiveFrom).getFullYear(),
        rate: tax.taxRate,
      }))
      .sort((a, b) => a.year - b.year)
      .slice(-6) // Last 6 years
  }, [item.taxData])

  return (
    <div className={clsx(
      "overflow-hidden rounded-xl border-2 transition-all",
      item.isNotPreferential
        ? "border-[#e5e5e5] bg-gray-50"
        : "border-[#e5e5e5] bg-white hover:border-[#1E40AF]"
    )}>
      {/* Header */}
      <button
        onClick={onToggle}
        className="flex w-full items-center justify-between p-5 text-left"
      >
        <div className="flex items-center gap-4">
          {/* Badge */}
          <div className={clsx(
            "flex p-4  items-center justify-center rounded-lg text-xs font-bold text-white",
            "bg-[#1E40AF]"
          )}>
            {item.key}
          </div>

          {/* Info */}
          <div>
            <div className={clsx(
              "text-base font-bold"
            )}>{item.key}</div>
            <div className={clsx(
              "flex items-center gap-3 text-sm"
            )}>
              <span className="flex items-center gap-1">
                <svg
                  className="size-3.5"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
                  <path
                    fillRule="evenodd"
                    d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z"
                    clipRule="evenodd"
                  />
                </svg>
                {item.form}
              </span>
              <span className="flex items-center gap-1">
                <svg
                  className="size-3.5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                {item.countries.length} nước
                {item.excludedCountryNames.length > 0 && (
                  <span className="text-[#DC2626]">
                    {' '}(loại trừ {item.excludedCountryNames.length})
                  </span>
                )}
              </span>
            </div>
          </div>
        </div>

        {/* Rate */}
        <div className="flex items-center gap-4">
          <div className="text-right">
            <div className={clsx(
              "text-xs font-medium uppercase",
              item.isNotPreferential ? "text-gray-400" : "text-[#525252]"
            )}>
              Hiện tại
            </div>
            <div className={clsx(
              "text-xl font-extrabold uppercase",
              item.isNotPreferential ? "text-gray-500" : "text-[#1E40AF]"
            )}>
              {item.isNotPreferential ? 'Không được hưởng ưu đãi' : (item.rate !== null ? `${item.rate}%` : 'N/A')}
            </div>
          </div>
          <ChevronDown
            className={clsx(
              'size-5 text-[#525252] transition-transform',
              isExpanded && 'rotate-180'
            )}
          />
        </div>
      </button>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="border-t-2 border-[#f5f5f5] bg-white p-5">
          {/* Row 1: Agreement Name */}
          <div className="mb-5">
            <div className="mb-1 text-md uppercase text-[#525252] font-bold">
              Hiệp định {item.key} :
            </div>
            <div className="text-base  text-[#171717]">
              {item.agreement}
            </div>
          </div>

          {/* 2x2 Grid */}
          <div className="mb-5 grid gap-5 md:grid-cols-2">
            {/* Top Left: Tax Rate */}
            <div>
              <div className="mb-1 text-md uppercase font-bold">
                Thuế suất :
              </div>
              {item.isNotPreferential && item.noteText ? (
                <div className="text-sm  text-[#171717]">
                  {item.noteText}
                </div>
              ) : (
                <div className="text-lg  text-[#1E40AF]">
                  {item.rate !== null && item.rate >= 0 ? `${item.rate}%` : 'N/A'}
                </div>
              )}
            </div>

            {/* Top Right: Effective Period */}
            <div>
              <div className="mb-1 text-md  uppercase font-bold">
                Thời gian áp dụng :
              </div>
              <div className="text-sm  ">
                {item.effectiveFrom
                  ? new Date(item.effectiveFrom).toLocaleDateString('vi-VN')
                  : 'N/A'}
                {' - '}
                {item.effectiveTo
                  ? new Date(item.effectiveTo).toLocaleDateString('vi-VN')
                  : 'N/A'}
              </div>
            </div>

            {/* Bottom Left: Countries */}
            {item.countries.length > 0 && (
              <div>
                <div className="mb-2 uppercase font-bold">
                  Quốc gia áp dụng
                </div>
                <div className="flex flex-wrap gap-2">
                  {item.countries.map((country, index) => (
                    <span
                      key={index}
                      className="rounded-md border border-[#e5e5e5] bg-white px-2.5 py-1 text-xs font-medium text-[#171717]"
                    >
                      {getCountryNameById(country.countryId)}
                    </span>
                  ))}
                </div>

                {/* Excluded Countries - placed under applied countries */}
                {item.excludedCountryNames.length > 0 && (
                  <div className="mt-3">
                    <div className="mb-2  uppercase font-semibold text-[#DC2626]">
                      Quốc gia loại trừ
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {item.excludedCountryNames.map((countryName, index) => (
                        <span
                          key={index}
                          className="rounded-md border border-[#FECACA] bg-[#FEF2F2] px-2.5 py-1 text-xs font-medium text-[#991B1B]"
                        >
                          {countryName}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Bottom Right: Legal Documents */}
            {item.table?.vbpl && item.table.vbpl.length > 0 && (
              <div>
                <div className="mb-2 uppercase font-bold">
                  Văn bản pháp lý :
                </div>
                <div className="text-sm  text-[#1E40AF]">
                  {item.table.vbpl.map((vbpl, i) => (
                    <span key={i}>
                      <a
                        href={vbpl.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:underline"
                      >
                        {vbpl.code}
                      </a>
                      {i < (item.table?.vbpl?.length ?? 0) - 1 && ', '}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Lưu ý - Only show for non-preferential */}
          {item.isNotPreferential && item.noteText && (
            <div className="mb-5 rounded-lg border-2 border-[#fef3c7] bg-[#fffbeb] p-4">
              <div className="mb-2 flex items-center gap-2 text-sm font-bold text-[#92400e]">
                <svg
                  className="size-5 text-[#f59e0b]"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
                Lưu ý
              </div>
              <div className="text-sm text-[#92400e]">
                {item.noteText}
              </div>
            </div>
          )}

          {/* Điều kiện hưởng ưu đãi - Only show for preferential */}
          {!item.isNotPreferential && (
            <div className="mb-5 rounded-lg border-2 border-[#e5e5e5] bg-[#fafafa] p-4">
              <div className="mb-3 flex items-center gap-2 text-sm font-bold text-[#171717]">
                <svg
                  className="size-4 text-[#1E40AF]"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                Điều kiện hưởng ưu đãi
              </div>
              <div className="space-y-2 text-sm">
                <div>
                  <span className="font-semibold text-[#171717]">Form C/O:</span>{' '}
                  <span className="text-[#525252]">
                    {item.form} ({item.key} C/O)
                  </span>
                </div>
                {item.conditions.length > 0 ? (
                  item.conditions.map((condition, index) => (
                    <div key={index}>
                      <span className="font-semibold text-[#171717]">
                        {index === 0 ? 'Điều kiện xuất xứ:' : index === 1 ? 'Nguồn gốc nhập khẩu:' : index === 2 ? 'Yêu cầu khác:' : ''}
                      </span>{' '}
                      <span className="text-[#525252]">{condition}</span>
                    </div>
                  ))
                ) : (
                  <div>
                    <span className="font-semibold text-[#171717]">
                      Điều kiện xuất xứ:
                    </span>{' '}
                    <span className="text-[#525252]">≥40% RVC hoặc CTH</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Chart - Diễn biến thuế suất - Only show for preferential */}
          {!item.isNotPreferential && taxHistory.length > 1 && (
            <div className="mb-5 rounded-lg  bg-white p-4">
              <div className="mb-4 text-sm font-bold">
                Diễn biến thuế suất
                <span className="text-sm font-normal text-[#878484]"> (Để xem chi tiết, click vào nút Chi tiết diễn biến dưới đây)</span>
              </div>

              <TaxRateChartv2 data={taxHistory} />
            </div>
          )}

          {/* Button Chi tiết thuế suất - Only show for preferential */}
          {!item.isNotPreferential && (
            <div className="flex justify-center">
              <a
                href={`#nkuddb-${item.key}`}
                className="inline-flex items-center gap-2 rounded-lg bg-[#1E40AF] p-2  font-semibold text-white transition-all hover:bg-[#1e3a8a] active:scale-95"
              >
                <svg
                  className="size-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                Chi tiết thuế suất
              </a>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
